import { Link } from "@tanstack/react-router";

import { articleBySlug } from "../lib/content";
import {
  type VerseTopic,
  verseMinutes,
  verseOutline,
  verseTopicBySlug,
} from "../lib/verses";
import { formatDate } from "./ArticleBody";
import { Contents, FaqList, renderBlock, ShortAnswer } from "./ProseBlocks";
import { StoreCta } from "./StoreCta";

/**
 * One topical collection of verses.
 *
 * ── Why the note sits under the verse and not beside it ──────────────────────
 *
 * Because the passage is what the reader came for and the note is the reason
 * the page deserves to exist. Putting the commentary first — the usual shape of
 * these pages, a paragraph of throat-clearing before each quotation — makes the
 * reader work for the thing they searched for, and they leave.
 *
 * The numbering is real `<ol>` markup rather than a decoration: this IS an
 * ordered list of ten things, people cite them by number when they send one to
 * somebody, and a screen reader announcing "list, ten items" tells you more
 * about this page than any heading on it.
 */

function Pick({
  index,
  text,
  reference,
  note,
}: {
  index: number;
  text: string;
  /** Named `reference` rather than `ref`, which React reserves as a prop. */
  reference: string;
  note: string;
}) {
  return (
    <li className="brutal scroll-mt-24 p-5 sm:p-6" id={`verse-${index + 1}`}>
      <div className="flex items-baseline gap-3">
        <span
          aria-hidden="true"
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-pill border-[2.5px] border-ink bg-surface font-display text-sm font-bold text-ink"
        >
          {index + 1}
        </span>
        <p className="font-display text-sm font-bold tracking-wide text-scripture uppercase">
          {reference}
        </p>
      </div>
      {/* Navy and a left rule, the same treatment scripture gets everywhere
          else on this site: it is not an action (docs/DESIGN-SYSTEM.md §9.2). */}
      <figure className="mt-4 border-l-[3px] border-scripture bg-scripture-wash/60 py-4 pr-4 pl-5">
        <blockquote className="font-display text-[clamp(1.05rem,2.5vw,1.25rem)] leading-snug text-scripture">
          “{text}”
        </blockquote>
      </figure>
      <p className="mt-4 leading-[1.75] text-body">{note}</p>
    </li>
  );
}

function RelatedTopics({ slugs }: { slugs: string[] }) {
  const topics = slugs.map(verseTopicBySlug).filter((t) => t !== undefined);
  if (topics.length === 0) return null;
  return (
    <section className="mt-14 border-t-[2.5px] border-ink pt-10">
      <h2 className="font-display text-2xl font-bold text-ink">
        More collections
      </h2>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {topics.map((topic) => (
          <li key={topic.slug}>
            <Link
              to="/verses/$slug"
              params={{ slug: topic.slug }}
              className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-lg leading-snug font-bold text-ink">
                {topic.h1}
              </span>
              <span className="text-sm leading-relaxed text-ink2">
                {topic.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RelatedArticles({ slugs }: { slugs: string[] }) {
  const articles = slugs.map(articleBySlug).filter((a) => a !== undefined);
  if (articles.length === 0) return null;
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl font-bold text-ink">Read next</h2>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {articles.map((a) => (
          <li key={a.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: a.slug }}
              className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-lg leading-snug font-bold text-ink">
                {a.h1}
              </span>
              <span className="text-sm leading-relaxed text-ink2">
                {a.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function VerseCollection({ topic }: { topic: VerseTopic }) {
  return (
    <article className="mx-auto w-full max-w-[68ch]">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        {formatDate(topic.published)}
        {topic.updated ? ` · updated ${formatDate(topic.updated)}` : ""} ·{" "}
        {topic.verses.length} passages · {verseMinutes(topic)} min read
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        {topic.h1}
      </h1>
      <p className="mt-4 text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-ink2">
        {topic.standfirst}
      </p>

      <ShortAnswer takeaways={topic.takeaways} />
      <Contents headings={verseOutline(topic)} />

      {topic.intro.map(renderBlock)}

      <section className="mt-12">
        <h2
          id="the-passages"
          className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
        >
          The passages
        </h2>
        <ol className="mt-6 flex flex-col gap-5">
          {topic.verses.map((pick, i) => (
            <Pick
              key={pick.verse.ref}
              index={i}
              text={pick.verse.text}
              reference={pick.verse.ref}
              note={pick.note}
            />
          ))}
        </ol>
      </section>

      {topic.pray.map(renderBlock)}

      {topic.faq ? <FaqList items={topic.faq} /> : null}

      <StoreCta from={`verses:${topic.slug}`} />

      <RelatedTopics slugs={topic.relatedTopics ?? []} />
      <RelatedArticles slugs={topic.related} />
    </article>
  );
}
